<template>
	<article :id="'question-' + activeQuestion.TEST_QUESTION_RID" class="question-display">
		<div class="sr-only">
			<p>
				{{ $t('MAIN_CONTENT.testTaker.questionNumber', { number: activeQuestion.index + 1 } ) }}
			</p>
		</div>
		<div class="pull-right">
			<span class="pill pill-dark">
				<strong>{{ activeQuestion.TOTAL_POINTS }}</strong> {{ $t('MAIN_CONTENT.ShortFillQuestion.ptsPossible') }}
			</span>
		</div>

		<div class="question-type-instructions">
			<strong>{{ activeQuestion.QUESTION_NAME }}</strong>
			<span class="hint">
				{{ $t('MAIN_CONTENT.ShortFillQuestion.enterMessage') }}
			</span>
		</div>

		<h3 class="question-text" v-html="activeQuestion.QUESTION_TEXT" />

		<div class="answers">
			<div :class="getAnswerClass()">
				<div :class="[ 'answer-number',{ selected:answeredByStudent() } ]">
					<i v-if="answeredByStudent()" class="fas fa-arrow-circle-right" />
					<div v-if="isGraded() && isCorrect()" class="badge badge-dark answer-points float-right">
						{{ $t('MAIN_CONTENT.testTaker.points', { points: studentAnswer[ 0 ].POINTS_EARNED.toFixed(2) } ) }}
					</div>
				</div>
				<div class="answer-text form">
					<input v-if="( !isGraded( studentAnswer ) || allowChanges ) && testMode !== 'grading'"
									ref="answerRef"
									v-model="answerData.answer[ 0 ].TEST_QUESTION_ANSWER_TEXT" 
									class="form-control"
									@blur="answerTheQuestion( answerData )">
					<div v-else>
						{{ answerData.answer[ 0 ].TEST_QUESTION_ANSWER_TEXT }}
					</div>
				</div>
				<div class="grade-result">
					<i :class="getFeedbackIcon()" />
				</div>
			</div>
		</div>

		<question-summary />
		
		<test-buttons v-if="testMode !== 'grading'" @answer-save="submitAnswer" />

		<div v-else>
			{{ $t('MAIN_CONTENT.NumericQuestion.acceptableAnswers') }}
			<ul>
				<li v-for="(ans,index) in activeQuestionAnswers" :key="index">
					<span>
						{{ans.ANSWER_TEXT}}
					</span>
					<span class="badge">
						{{ $t('MAIN_CONTENT.NumericQuestion.points', { points: ans.POINTS } ) }}
					</span>
				</li>
			</ul>
		</div>
	</article>
</template>

<script>
import { mapState,mapActions,mapMutations } from "vuex";
import testButtons from "./testButtons.vue";
import questionSummary from "./questionSummary.vue";

export default {
		components : { testButtons, questionSummary },
		props      : {
				testPreview: {
						type     : Object,
						required : true
				},
				activeQuestion: {
						type     : Object,
						required : true
				},
				studentAnswer: {
						type    : Array,
						default : function(){
								return [];
						}
				},
				studentAnswers: {
						type    : Object,
						default : function(){
								return {};
						}
				},
				blankAnswer: {
						type    : Object,
						default : function(){
								return {};
						}
				},
				testMode: {
						type    : String,
						default : "student"
				}
		},
		data (){
				return{
						answerData: {
								testId     : "",
								questionId : "",
								answer     : []
						}
				};
		},
		computed: {
				allowChanges (){
						return this.testPreview.TEST.ALLOW_ANSWER_CHANGE;
				},
				activeQuestionAnswers (){
						return this.testPreview.ANSWERS.filter( ans =>{
								return ans.TEST_QUESTION_RID === this.activeQuestion.TEST_QUESTION_RID; 
						} );
				},
				answerChoice (){
						return this.activeQuestionAnswers[ 0 ];
				}
		},
		watch: {
				activeQuestion: {
						immediate: true,
						handler: function(newValue, oldValue) {
								this.answerData.questionId = this.activeQuestion.TEST_QUESTION_RID;
								this.answerData.testId = this.activeQuestion.TEST_RID;
								this.populateForm();
								this.$nextTick(()=>{
										try{
												document.getElementById( "testZone" ).focus();
										}catch(e){}
								})
						},
				},
		},
		methods: {
				...mapMutations( [ "updateState","setUnsaved" ] ),
				...mapActions( [ "update","scoreAnswer","postAnswer" ] ),

				isSelected ( answer ){
						return this.studentAnswer.map( ans =>{
								return ans.ANSWER_RID; 
						} ).indexOf( answer.ANSWER_RID ) > -1;
				},
				// returns true if question has been graded
				isGraded (){
						return this.testMode === "grading" || (this.studentAnswer && this.studentAnswer.filter( ans =>{
								return ans.QUESTION_GRADED_STATUS; 
						} ).length > 0);
				},
				// will return css class for check if correct and x for incorrect for graded answers
				getFeedbackIcon (){
						if( !this.isGraded() ) return;
						return this.isCorrect() ? "fa fas fa-check-circle" :"fa fas fa-times-circle";
				},
				// returns css class if graded correct
				getAnswerClass (){
						let cls = "answer";
						if( !this.isGraded() ){
								cls += " clickable";
						}else{
								cls += this.isCorrect() ? " correct" : " incorrect";
						}
						return cls;
				},
				// if the question is answered ( may or may not be correct )
				answeredByStudent (){
						return  Object.prototype.hasOwnProperty.call( this.studentAnswers, this.activeQuestion.TEST_QUESTION_RID ); 
				},
				// returns the value of the graded answer correct flag
				isCorrect (){
						if( ! this.isGraded ) return;
						return this.studentAnswer[ 0 ].ANSWER_CORRECT_FLG;
				},
				prepareAnswer (){
						// populate payload with activeQuestion id to use as a key
						this.answerData.questionId = this.activeQuestion.TEST_QUESTION_RID;
						this.answerData.testId = this.activeQuestion.TEST_RID;
						this.answerData.questionType = this.activeQuestion.QUESTION_TYPE;

						// populate it
						this.answerData.answer[ 0 ].TEST_RID = this.activeQuestion.TEST_RID;
						this.answerData.answer[ 0 ].TEST_QUESTION_RID = this.activeQuestion.TEST_QUESTION_RID;

				},
				answerTheQuestion ( answer ){
						// question has been answered, so no changes
						if( this.isGraded() && !this.allowChanges ) return;
						// save the answer
						this.prepareAnswer();
						// clear this answer from unsaved
						this.setUnsaved( { key: answer.questionId, value: JSON.parse( JSON.stringify(this.answerData)) } );
						this.postAnswer( { 
										"key": this.answerData.questionId,
										"data": JSON.stringify(this.answerData),
										"queryParams":{},
										"routeParams":{ 
												action: "answer-question", 
												testRid: this.$route.params.testRid
										} 
								}
						).then(()=>{
								// clear this answer from unsaved
								this.setUnsaved( { key: answer.questionId, value: "" } );
						});
				},
				populateForm (){
						// start with the blank answer obj
						let blankAns = [];
						blankAns.push(JSON.parse( JSON.stringify( this.blankAnswer ) ));
						this.$set(this.answerData, "answer", blankAns);

						if( this.studentAnswer ){
								this.$set(this.answerData, "answer", this.studentAnswer);
						}
				},
				focusInput ( inputRef ) {
						this.$refs[inputRef].focus();
				},
				submitAnswer (){
						if( !this.answerData.questionId ) return;
						// tracking unsaved responses
						this.setUnsaved( { key: this.answerData.questionId, value: "" } );
						this.scoreAnswer( { 
										"key": this.answerData.questionId,
										"data": JSON.stringify(this.answerData),
										"queryParams":{},
										"routeParams":{ 
												action: "score-question", 
												testRid: this.$route.params.testRid
										} 
								}
						);
				}
		},
		created (){
				this.populateForm();
		},
		mounted (){
				try{
						document.getElementById( "testZone" ).focus();
				}catch(e){}
		}
};
</script>
